// Dashboard.js - Modified version with integrated Quote Modal

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Topbar from '../components/Topbar';
import DashboardStats from '../components/DashboardStats';
import DashboardTable from '../components/DashboardTable';
import DashboardModals from '../components/DashboardModals';
import DashboardFilters from '../components/DashboardFilters';
import NewsletterManager from '../components/NewsletterManager';
import LoadingWidget from '../../../components/LoadingWidget';
import AlertMessage from '../../../components/AlertMessage';
import Modal from '../../../components/Modal';
import {
  getStatusBadgeColor,
  formatDate,
  formatCurrency,
  getDisplayName,
  getField
} from '../components/DashboardHelpers';
import { useAllDashboardData } from '../hooks/AdminDashboardQueries';
import axios from 'axios';
import { ResponsivePie } from '@nivo/pie';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    service_type: '',
    date_from: '',
    date_to: '',
    role: '',
    account_status: '',
    followup_type: '',
    document_type: '',
    quotation_id: '',
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false); // New state for quote modal
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertState, setAlertState] = useState(null); // { type, title, message, duration, onDismiss }
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient();

  // Handle token expiry: show warning alert then redirect to /login
  useEffect(() => {
    const handleTokenExpired = () => {
      queryClient.clear();
      setAlertState({
        type: 'warning',
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again.',
        duration: 2500,
        onDismiss: () => navigate('/login', { replace: true }),
      });
    };
    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => window.removeEventListener('tokenExpired', handleTokenExpired);
  }, [navigate, queryClient]);

  // Proactive JWT expiry check — runs on mount and every 30 seconds
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        // Decode the payload without verifying the signature (client-side only)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000; // convert to ms

        if (Date.now() >= expiresAt) {
          // Token is already expired — clean up and redirect
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('is_superuser');
          localStorage.removeItem('authTokenStoredAt');
          queryClient.clear();
          window.dispatchEvent(new CustomEvent('tokenExpired'));
        }
      } catch {
        // Malformed token — treat as expired
        window.dispatchEvent(new CustomEvent('tokenExpired'));
      }
    };

    checkTokenExpiry(); // Check immediately on mount
    const interval = setInterval(checkTokenExpiry, 30_000); // Then every 30s
    return () => clearInterval(interval);
  }, [queryClient]);

  // Use React Query to fetch all data
  const {
    stats,
    quotations,
    users,
    solarQuotations,
    electricalQuotations,
    electricalServices,
    followups,
    documents,
    subscribers,
    isLoading,
    isFetching,
    refetchAll
  } = useAllDashboardData(API_BASE_URL, queryClient);

  // Listen for custom event from Topbar notification
  useEffect(() => {
    const handleViewQuotationFromNotification = async (event) => {
      const { quotationId, notificationId } = event.detail;

      try {
        // First, mark the notification as read
        const token = localStorage.getItem('authToken');
        if (notificationId) {
          try {
            await axios.post(`${API_BASE_URL}/api/notifications/${notificationId}/mark_read/`, {}, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          } catch (error) {
          }
        }

        // Try to find the quotation in cached data first
        const existingQuotation = quotations.find(q => q.id === quotationId);

        if (existingQuotation) {
          setSelectedItem(existingQuotation);
          setModalType('quotation');
          setShowViewModal(true);
          setActiveTab('quotations');
        } else {
          // If not found, fetch it from the API
          const response = await axios.get(`${API_BASE_URL}/api/quotations/${quotationId}/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.data) {
            setSelectedItem(response.data);
            setModalType('quotation');
            setShowViewModal(true);
            setActiveTab('quotations');
          }
        }
      } catch (error) {
        // Show error message to user
        setErrorModal({ show: true, title: 'Quotation Not Found', message: 'Unable to load quotation. It may have been deleted or you may not have permission to view it.' });
      }
    };

    window.addEventListener('viewQuotationFromNotification', handleViewQuotationFromNotification);

    return () => {
      window.removeEventListener('viewQuotationFromNotification', handleViewQuotationFromNotification);
    };
  }, [API_BASE_URL, quotations]);

  // CRUD handlers
  // In Dashboard.js, update the handleAddItem function:

  // In Dashboard.js, update the handleAddItem function:

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (modalType === 'user') {
      // Password validation
      if (newItem.password !== newItem.password2) {
        showAlert('error', 'Validation Error', 'Passwords do not match');
        return;
      }

      if (newItem.password.length < 8) {
        showAlert('error', 'Validation Error', 'Password must be at least 8 characters long');
        return;
      }

      if (!/(?=.*[a-z])/.test(newItem.password)) {
        showAlert('error', 'Validation Error', 'Password must contain at least one lowercase letter');
        return;
      }

      if (!/(?=.*[A-Z])/.test(newItem.password)) {
        showAlert('error', 'Validation Error', 'Password must contain at least one uppercase letter');
        return;
      }

      if (!/(?=.*\d)/.test(newItem.password)) {
        showAlert('error', 'Validation Error', 'Password must contain at least one number');
        return;
      }

      if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(newItem.password)) {
        showAlert('error', 'Validation Error', 'Password must contain at least one special character');
        return;
      }

      // Username validation
      if (!newItem.username || newItem.username.trim() === '') {
        showAlert('error', 'Validation Error', 'Username is required');
        return;
      }

      if (newItem.username.length < 3) {
        showAlert('error', 'Validation Error', 'Username must be at least 3 characters');
        return;
      }

      if (!/^[a-zA-Z0-9_]+$/.test(newItem.username)) {
        showAlert('error', 'Validation Error', 'Username can only contain letters, numbers, and underscores');
        return;
      }

      // Email validation
      if (!newItem.email || newItem.email.trim() === '') {
        showAlert('error', 'Validation Error', 'Email is required');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newItem.email)) {
        showAlert('error', 'Validation Error', 'Please enter a valid email address');
        return;
      }

      // Phone validation (optional)
      if (newItem.phone && newItem.phone.trim() !== '') {
        const cleanedPhone = newItem.phone.replace(/[^\d+]/g, '');
        const phoneRegex = /^\+?[1-9]\d{0,14}$/;
        if (!phoneRegex.test(cleanedPhone)) {
          showAlert('error', 'Validation Error', 'Please enter a valid phone number');
          return;
        }
      }

      try {
        const token = localStorage.getItem('authToken');

        const isStaff = newItem.is_staff || false;
        const endpoint = isStaff
          ? `${API_BASE_URL}/rest-auth/admin/create-user/`
          : `${API_BASE_URL}/rest-auth/register/`;

        setIsProcessing(true);

        const response = await axios.post(
          endpoint,
          {
            username: newItem.username.trim(),
            email: newItem.email.trim().toLowerCase(),
            first_name: newItem.first_name?.trim() || '',
            last_name: newItem.last_name?.trim() || '',
            phone: newItem.phone?.trim() || '',
            address: newItem.address?.trim() || '',
            password: newItem.password,
            password2: newItem.password2,
            is_staff: isStaff
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 201) {
          // Close modal FIRST so its unmount happens before the alert mounts,
          // preventing a re-render from resetting the AlertMessage timer.
          const createdUsername = newItem.username;
          const createdEmail = newItem.email;
          setNewItem({});
          setShowAddModal(false);
          setModalType('');

          if (isStaff) {
            showAlert('success',
              'Staff User Created',
              `Staff user "${createdUsername}" has been created successfully and can log in immediately.`,
              2500
            );
          } else {
            showAlert('success',
              'User Created',
              `User account created successfully! A verification email has been sent to ${createdEmail}. The user must verify their email before logging in.`,
              4000
            );
          }

          // Refresh the users list
          setTimeout(() => {
            refetchAll();
          }, 500); // Small delay to ensure backend processing
        }
      } catch (error) {

        if (error.response && error.response.data) {
          // Handle validation errors from backend
          const errors = error.response.data;
          let errorMessage = 'Failed to create user:\n';

          // Format error messages nicely
          Object.keys(errors).forEach(key => {
            if (Array.isArray(errors[key])) {
              // Format field name for display
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${errors[key][0]}`;
            } else if (typeof errors[key] === 'string') {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessage += `\n• ${fieldName}: ${errors[key]}`;
            } else if (key === 'non_field_errors' && Array.isArray(errors[key])) {
              errorMessage += `\n• ${errors[key][0]}`;
            }
          });

          showAlert('error', 'Registration Error', errorMessage, 4000);
        } else if (error.request) {
          // Request was made but no response received
          showAlert('error', 'Connection Error', 'Unable to connect to the server. Please check your connection and try again.');
        } else {
          // Something else happened
          showAlert('error', 'Error', 'An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
    } else if (modalType === 'document') {
      // --- Document upload (multipart/form-data) ---
      if (!newItem.quotation) {
        showAlert('error', 'Validation Error', 'Please select a quotation to attach this document to.');
        return;
      }
      if (!newItem.document_type) {
        showAlert('error', 'Validation Error', 'Please select a document type.');
        return;
      }
      if (!newItem._file) {
        showAlert('error', 'Validation Error', 'Please select a file to upload.');
        return;
      }

      try {
        setIsProcessing(true);
        const token = localStorage.getItem('authToken');

        const formData = new FormData();
        formData.append('quotation', newItem.quotation);
        formData.append('document_type', newItem.document_type);
        formData.append('file', newItem._file);
        if (newItem.description) {
          formData.append('description', newItem.description);
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/documents/`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              // Do NOT set Content-Type — axios sets it automatically with the correct boundary for FormData
            }
          }
        );

        if (response.status === 201) {
          const fileName = newItem._file.name;
          const quotationId = newItem.quotation;
          setNewItem({});
          setShowAddModal(false);
          setModalType('');
          showAlert('success', 'Document Uploaded', `"${fileName}" has been attached to quotation #${quotationId} successfully.`, 2500);
          setTimeout(() => refetchAll(), 300);
        }
      } catch (error) {
        if (error.response?.data) {
          const errors = error.response.data;
          const msg = Object.entries(errors)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
            .join('\n');
          showAlert('error', 'Upload Failed', msg, 2500);
        } else {
          showAlert('error', 'Upload Failed', 'Failed to upload document. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
    } else {
      // Handle other item types
      try {
        refetchAll();
        const createdModalType = modalType;
        setShowAddModal(false);
        setNewItem({});
        setModalType('');
        showAlert('success', 'Success', `${createdModalType} created successfully!`);

      } catch (error) {
        showAlert('error', 'Error', `Failed to create ${modalType}. Please try again.`);
      }
    }
  };

  const handleEditItem = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (modalType === 'subscriber' && selectedItem) {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_BASE_URL}/newsletter/subscribers/${selectedItem.id}/`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();
        if (response.ok && data.success) {
          const action = data.is_active ? 'resubscribed' : 'unsubscribed';
          showAlert('success', 'Subscriber Updated', `Subscriber has been ${action} successfully.`);
          if (refetchAll) refetchAll();
          setShowEditModal(false);
          setSelectedItem(null);
          setModalType('');
        } else {
          showAlert('error', 'Update Failed', data.message || 'Failed to update subscriber.');
        }
      } catch (error) {
        showAlert('error', 'Update Failed', 'Failed to update subscriber. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      refetchAll();
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) {
      showAlert('error', 'Error', 'No item selected to delete');
      return;
    }

    setIsProcessing(true);
    try {
      const token = localStorage.getItem('authToken');

      if (modalType === 'user') {
        // Delete a user
        const response = await fetch(
          `${API_BASE_URL}/rest-auth/admin/delete-user/${selectedItem.id}/`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          showAlert('success', 'User Deleted', data.message);
          if (refetchAll) refetchAll();
          setShowDeleteModal(false);
          setSelectedItem(null);
          setModalType('');
        } else {
          showAlert('error', 'Delete Failed', data.error || 'Failed to delete user');
        }

      } else if (modalType === 'subscriber') {
        // Delete a newsletter subscriber
        const response = await fetch(
          `${API_BASE_URL}/newsletter/subscribers/${selectedItem.id}/`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          showAlert('success', 'Subscriber Deleted', data.message);
          if (refetchAll) refetchAll();
          setShowDeleteModal(false);
          setSelectedItem(null);
          setModalType('');
        } else {
          showAlert('error', 'Delete Failed', data.message || 'Failed to delete subscriber');
        }

      } else if (modalType === 'document') {
        // Delete a document
        const response = await fetch(
          `${API_BASE_URL}/api/documents/${selectedItem.id}/`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok || response.status === 204) {
          showAlert('success', 'Document Deleted', 'Document has been deleted successfully.');
          if (refetchAll) refetchAll();
          setShowDeleteModal(false);
          setSelectedItem(null);
          setModalType('');
        } else {
          const data = await response.json().catch(() => ({}));
          showAlert('error', 'Delete Failed', data.detail || data.error || 'Failed to delete document');
        }

      } else {
        // For solar-quotation / electrical-quotation rows, item.id is the sub-table PK.
        // We need the parent Quotation PK, which is stored in item.quotation.
        const quotationId =
          (modalType === 'solar-quotation' || modalType === 'electrical-quotation')
            ? selectedItem.quotation
            : selectedItem.id;

        const response = await fetch(
          `${API_BASE_URL}/api/quotations/${quotationId}/delete/`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          showAlert('success', 'Quotation Deleted', data.message);
          if (refetchAll) refetchAll();
          setShowDeleteModal(false);
          setSelectedItem(null);
          setModalType('');
        } else {
          showAlert('error', 'Delete Failed', data.error || 'Failed to delete quotation');
        }
      }
    } catch (error) {
      showAlert('error', 'Delete Failed', `Failed to delete ${modalType}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to show alert (passed to modals)
  const showAlert = (type, title, message, duration = 2500) => {
    setAlertState({ type, title, message, duration });
  };

  // Get filtered data based on active tab
  const getFilteredData = () => {
    let data = [];

    switch (activeTab) {
      case 'quotations':
        data = (quotations || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          const userName = item.user_info?.name || item.customer_name || item.customer?.name || '';
          const userEmail = item.user_info?.email || item.customer_email || item.customer?.email || '';

          return (
            userName.toLowerCase().includes(searchLower) ||
            userEmail.toLowerCase().includes(searchLower) ||
            (item.service_type && item.service_type.toLowerCase().includes(searchLower)) ||
            (item.status && item.status.toLowerCase().includes(searchLower))
          );
        });

        if (filters.status) {
          data = data.filter(item => item.status === filters.status);
        }
        if (filters.service_type) {
          data = data.filter(item => item.service_type === filters.service_type);
        }
        break;

      case 'users':
        data = (users || []).filter(item => {
          const searchLower = searchTerm.toLowerCase();
          const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
          const username = (item.username || '').toLowerCase();
          const email = (item.email || '').toLowerCase();
          const phone = item.phone || '';

          return (
            fullName.includes(searchLower) ||
            username.includes(searchLower) ||
            email.includes(searchLower) ||
            phone.includes(searchTerm)
          );
        });

        if (filters.role === 'staff') {
          data = data.filter(item => item.is_staff);
        } else if (filters.role === 'customer') {
          data = data.filter(item => !item.is_staff);
        }
        if (filters.account_status === 'active') {
          data = data.filter(item => item.is_active);
        } else if (filters.account_status === 'inactive') {
          data = data.filter(item => !item.is_active);
        }
        break;

      case 'solar-quotations':
        data = (solarQuotations || []).filter(item =>
          (item.quotation && item.quotation.toString().includes(searchTerm)) ||
          (item.system_type && item.system_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.actual_system_size_kw && item.actual_system_size_kw.toString().includes(searchTerm))
        );
        break;

      case 'electrical-quotations':
        data = (electricalQuotations || []).filter(item =>
          (item.quotation && item.quotation.toString().includes(searchTerm)) ||
          (item.property_type && item.property_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.property_size && item.property_size.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        break;

      case 'electrical-services':
        data = (electricalServices || []).filter(item =>
          (item.service_type && item.service_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.electrical_quotation && item.electrical_quotation.toString().includes(searchTerm)) ||
          (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        break;

      case 'followups':
        data = (followups || []).filter(item =>
          (item.notes && item.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.followup_type && item.followup_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.quotation && item.quotation.toString().includes(searchTerm))
        );

        if (filters.followup_type) {
          data = data.filter(item => item.followup_type === filters.followup_type);
        }
        if (filters.status) {
          data = data.filter(item => item.status === filters.status);
        }
        break;

      case 'documents':
        data = (documents || []).filter(item =>
          (item.document_type && item.document_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.quotation && item.quotation.toString().includes(searchTerm))
        );

        if (filters.document_type) {
          data = data.filter(item => item.document_type === filters.document_type);
        }
        if (filters.quotation_id) {
          data = data.filter(item => item.quotation && item.quotation.toString() === filters.quotation_id);
        }
        break;

      case 'subscribers':
        data = (subscribers || []).filter(item =>
          item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        break;

      default:
        data = [];
    }

    return data;
  };

  // Prepare pie chart data for service type breakdown
  const getServiceTypePieData = () => {
    const serviceTypes = ['solar', 'electrical', 'generator'];
    const colorMap = {
      'solar': '#F59E0B',
      'electrical': '#3B82F6',
      'generator': '#8B5CF6',
    };

    return serviceTypes.map((type) => {
      const count = (quotations || []).filter(q => q.service_type === type).length;
      const total = (quotations || []).length;
      const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;

      return {
        id: type.charAt(0).toUpperCase() + type.slice(1),
        label: type.charAt(0).toUpperCase() + type.slice(1),
        value: count,
        percentage: percentage,
        color: colorMap[type]
      };
    }).filter(item => item.value > 0);
  };

  const filteredData = getFilteredData();
  const pieData = getServiceTypePieData();

  return (
    <div className="min-h-screen section bg-gray-50 relative">
      <Topbar />

      {/* Alert Message */}
      {alertState && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          duration={alertState.duration}
          onDismiss={() => {
            setAlertState(prev => {
              if (prev?.onDismiss) prev.onDismiss();
              return null;
            });
          }}
        />
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <Modal
          title={errorModal.title}
          message={errorModal.message}
          onClick={() => setErrorModal({ show: false, title: '', message: '' })}
        />
      )}

      {/* Floating Loading Overlay - initial data load */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"></div>
          <div className="relative transform scale-150">
            <LoadingWidget />
          </div>
        </div>
      )}

      {/* Processing Overlay - delete / status updates */}
      {isProcessing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-sm"></div>
          <div className="relative transform scale-150">
            <LoadingWidget />
          </div>
        </div>
      )}

      <section className={`max-w-7xl mx-auto pt-20 px-4 sm:px-6 lg:px-8 py-8 ${(isLoading || isProcessing) ? 'pointer-events-none opacity-50' : ''}`}>
        {/* Stats Overview */}
        <DashboardStats
          stats={stats}
          formatCurrency={formatCurrency}
          isAdmin={true}
        />

        {/* Navigation Tabs */}
        <DashboardFilters
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          modalType={modalType}
          setModalType={setModalType}
          setShowAddModal={setShowAddModal}
          isFetching={isFetching}
          onRefresh={refetchAll}
        />

        {/* Data Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-800 capitalize">
                {activeTab === 'overview' ? 'Dashboard Overview' : activeTab === 'users' ? 'Users' : activeTab.replace('-', ' ')}
              </h2>
              {activeTab !== 'overview' && activeTab !== 'newsletter' && (
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  {filteredData.length} items
                </span>
              )}
            </div>

            {activeTab !== 'overview' && activeTab !== 'newsletter' && (
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={`Search ${activeTab === 'users' ? 'users' : activeTab.replace('-', ' ')}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
                  />
                </div>

                {activeTab !== 'solar-quotations' && activeTab !== 'electrical-quotations' && activeTab !== 'followups' && (
                  <button
                    onClick={() => {
                      if (activeTab === 'quotations') {
                        // Show the quote modal instead of navigating
                        setModalType('quotation');
                        setShowQuoteModal(true);
                      } else if (activeTab === 'documents') {
                        // Inject quotations list so the form can render a selector
                        setModalType('document');
                        setNewItem({ _quotationsList: quotations || [] });
                        setShowAddModal(true);
                      } else {
                        // For other tabs, use the simple add form
                        setModalType(activeTab.slice(0, -1));
                        setShowAddModal(true);
                      }
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center"
                  >
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="p-6">
            {activeTab === 'overview' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Quotations */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Quotations</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {(quotations || []).slice(0, 5).map((item) => (
                      <div
                        key={item.id}
                        className="mb-3 last:mb-0 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedItem(item);
                          setModalType('quotation');
                          setShowViewModal(true);
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.user_info?.name || item.customer_name || getField(item.customer, 'name') || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {getField(item, 'service_type_display', item.service_type)}
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(item.status)}`}>
                            {getField(item, 'status_display', item.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service Type Breakdown with Nivo Pie Chart */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Service Type Breakdown</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {pieData.length > 0 ? (
                      <div className="h-64">
                        <ResponsivePie
                          data={pieData}
                          margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
                          innerRadius={0.5}
                          padAngle={1}
                          cornerRadius={3}
                          activeOuterRadiusOffset={8}
                          colors={{ datum: 'data.color' }}
                          borderWidth={1}
                          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                          enableArcLinkLabels={true}
                          arcLinkLabelsSkipAngle={10}
                          arcLinkLabelsTextColor="#6B7280"
                          arcLinkLabelsThickness={2}
                          arcLinkLabelsColor={{ from: 'color' }}
                          arcLabelsSkipAngle={10}
                          arcLabelsTextColor="#111827"
                          legends={[
                            {
                              anchor: 'bottom',
                              direction: 'row',
                              justify: false,
                              translateX: 0,
                              translateY: 50,
                              itemsSpacing: 0,
                              itemWidth: 100,
                              itemHeight: 18,
                              itemTextColor: '#6B7280',
                              itemDirection: 'left-to-right',
                              symbolSize: 12,
                              symbolShape: 'circle',
                            }
                          ]}
                          tooltip={({ datum }) => (
                            <div className="bg-white p-2 rounded-lg shadow-lg border border-gray-200">
                              <div className="flex items-center">
                                <div
                                  className="w-3 h-3 rounded-full mr-2"
                                  style={{ backgroundColor: datum.color }}
                                />
                                <strong className="text-gray-900">{datum.label}</strong>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Count: {datum.value}
                              </div>
                              <div className="text-sm text-gray-600">
                                Percentage: {datum.data.percentage}%
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center">
                        <div className="text-center">
                          <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="mt-2 text-gray-500">No service type data available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : activeTab === 'newsletter' ? (
              <NewsletterManager />
            ) : (
              <DashboardTable
                activeTab={activeTab}
                filteredData={filteredData}
                setSelectedItem={setSelectedItem}
                setModalType={setModalType}
                setShowViewModal={setShowViewModal}
                setShowEditModal={setShowEditModal}
                setShowDeleteModal={setShowDeleteModal}
                getStatusBadgeColor={getStatusBadgeColor}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getField={getField}
              />
            )}
          </div>
        </div>
      </section>

      {/* Modals */}
      <DashboardModals
        showViewModal={showViewModal}
        showAddModal={showAddModal}
        showEditModal={showEditModal}
        showDeleteModal={showDeleteModal}
        showQuoteModal={showQuoteModal}
        selectedItem={selectedItem}
        modalType={modalType}
        newItem={newItem}
        setShowViewModal={setShowViewModal}
        setShowAddModal={setShowAddModal}
        setShowEditModal={setShowEditModal}
        setShowDeleteModal={setShowDeleteModal}
        setShowQuoteModal={setShowQuoteModal}
        setNewItem={setNewItem}
        setModalType={setModalType}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getDisplayName={getDisplayName}
        users={users || []}
        API_BASE_URL={API_BASE_URL}
        refetchAll={refetchAll}
        showAlert={showAlert}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default AdminDashboard;